import { Box, Drawer, IconButton, Stack, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { RuleLogsPanel } from './RuleLogsPanel';

interface RuleLogsDrawerProps {
  open: boolean;
  onClose: () => void;
  /** When `null`, the panel renders nothing (drawer is closed anyway). The
   *  drawer keeps mounted state so closing+reopening for the same rule
   *  preserves the expanded-payload set; switching rules resets it. */
  ruleId: string | null;
  /** Pair name shown in the header so the user knows which rule's logs
   *  they're looking at. */
  pairName?: string;
}

/**
 * Right-anchored drawer wrapping the per-rule dev-logs panel. Triggered
 * from the bug-report icon next to the expand chevron in each unwinding
 * row. Kept separate from the row's expanded state (which is for
 * calculations) so opening logs doesn't disturb the simulation breakdown
 * the operator may be reading.
 */
export const RuleLogsDrawer = ({
  open,
  onClose,
  ruleId,
  pairName,
}: RuleLogsDrawerProps) => {
  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      // Non-persistent: tap-outside dismisses, blocks page scroll while open.
      // Different from DrawerArbitrageProfit because the unwind page itself
      // is also scrollable behind it; persistent would let users scroll
      // away accidentally.
      // `keepMounted={false}` (the default) is critical here — the dev-logs
      // panel holds a live WebSocket subscription that fires multiple
      // setStates per second. Keeping it mounted while closed would burn
      // CPU on every WS push behind a hidden drawer.
      keepMounted={false}
      PaperProps={{
        sx: {
          width: { xs: '100vw', md: '60vw' },
          maxWidth: '900px',
          bgcolor: '#1a1a2e',
          color: 'white',
        },
      }}
    >
      <Box
        sx={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          spacing={1}
          sx={{
            px: 2,
            py: 1.25,
            borderBottom: '1px solid rgba(255,255,255,0.08)',
            flexShrink: 0,
          }}
        >
          <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, flex: 1 }}>
            Dev Logs
            {pairName && (
              <Box
                component="span"
                sx={{ color: 'grey.400', ml: 1, fontWeight: 400 }}
              >
                — {pairName}
              </Box>
            )}
          </Typography>
          <IconButton size="small" onClick={onClose} sx={{ color: 'grey.400' }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Stack>
        <Box sx={{ flex: 1, minHeight: 0, p: 1.5, overflow: 'hidden' }}>
          {ruleId ? (
            // `key` ensures internal state (expanded payloads, scroll
            // position) resets when the user switches between rules.
            <RuleLogsPanel key={ruleId} ruleId={ruleId} />
          ) : (
            <Typography
              sx={{ p: 2, fontSize: '0.75rem', color: 'grey.500' }}
            >
              No rule selected.
            </Typography>
          )}
        </Box>
      </Box>
    </Drawer>
  );
};
